import React, { useState, useEffect } from 'react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { auth, db, logAnalyticsEvent } from './firebase';
import { onAuthStateChanged, signOut, isSignInWithEmailLink, signInWithEmailLink } from 'firebase/auth';
import { ref, onValue, update } from 'firebase/database';
import Login from './components/Login';
import Register from './components/Register';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import EditPropertyModal from './components/EditPropertyModal';
import OwnerDashboard from './components/OwnerDashboard';
import TouristDashboard from './components/TouristDashboard';
import AdminDashboard from './components/AdminDashboard';
import Profile from './components/Profile';
import Notifications from './components/Notifications';
import VerifyEmail from './components/VerifyEmail';
import Homepage from './components/Homepage';
import PoliciesPropertyDetails from './components/PoliciesPropertyDetails';
import ResubmitDocuments from './components/ResubmitDocuments';
import ChatHeadBubble from './components/ChatHeadBubble';
import { LogOut, Bell, User, LayoutDashboard, Moon, Sun, Home, ShieldAlert } from 'lucide-react';
import logo from './assets/ResortConnectLogo.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  tooltipAnchor: [16, -28]
});
L.Marker.prototype.options.icon = DefaultIcon;

function App() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authView, setAuthView] = useState('home');
  const [oobCode, setOobCode] = useState(null);
  const [view, setView] = useState('dashboard');
  const [dashboardKey, setDashboardKey] = useState(Date.now());
  const [unreadCount, setUnreadCount] = useState(0);
  const [verifyingLink, setVerifyingLink] = useState(false);
  const [requireEmailForLink, setRequireEmailForLink] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [proceedSocial, setProceedSocial] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('isDarkMode');
    return saved ? JSON.parse(saved) : false;
  });

  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
      localStorage.setItem('isDarkMode', 'true');
    } else {
      document.documentElement.removeAttribute('data-theme');
      localStorage.setItem('isDarkMode', 'false');
    }
  }, [isDarkMode]);

  useEffect(() => {
    if (isSignInWithEmailLink(auth, window.location.href)) {
      let email = window.localStorage.getItem('emailForSignIn');
      if (!email) {
        setRequireEmailForLink(true);
        return;
      }
      setVerifyingLink(true);
      signInWithEmailLink(auth, email, window.location.href)
        .then((result) => {
          window.localStorage.removeItem('emailForSignIn');
          setAuthView('resetPassword');
          window.history.replaceState({}, document.title, window.location.pathname);
          setVerifyingLink(false);
        })
        .catch((error) => {
          console.error('Error signing in with email link', error);
          if (error.code === 'auth/invalid-action-code') {
            setLinkError('This password reset link has expired or has already been used. Please request a new link.');
          } else {
            setLinkError(error.message);
          }
          setVerifyingLink(false);
        });
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get('mode');
    const code = urlParams.get('oobCode');
    if (mode === 'resetPassword' && code) {
      setOobCode(code);
      setAuthView('resetPassword');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const paymentSuccess = urlParams.get('payment_success');
    const bookingId = urlParams.get('booking_id');

    if (paymentSuccess === 'true' && bookingId) {
      const bookingRef = ref(db, `bookings/${bookingId}`);
      update(bookingRef, {
        paymentStatus: 'paid',
        status: 'Confirmed'
      }).then(() => {
        alert('Payment Successful! Your booking is now Confirmed.');
        window.history.replaceState({}, document.title, window.location.pathname);
      }).catch(err => {
        console.error("Failed to update booking status", err);
      });
    }
  }, []);

  useEffect(() => {
    // Log screen / view transitions
    const screenName = user ? `app_${view}` : `auth_${authView}`;
    logAnalyticsEvent('screen_view', {
      firebase_screen: screenName,
      firebase_screen_class: screenName,
    });
  }, [user, view, authView]);

  useEffect(() => {
    const handleOpenChatGlobal = () => {
      if (view !== 'dashboard') {
        setView('dashboard');
      }
    };
    window.addEventListener('rc-open-chat', handleOpenChatGlobal);
    return () => window.removeEventListener('rc-open-chat', handleOpenChatGlobal);
  }, [view]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      if (user) {
        setLoading(true);
        logAnalyticsEvent('login', { method: user.providerData?.[0]?.providerId || 'password' });
        const userRef = ref(db, `users/${user.uid}`);
        onValue(userRef, (snapshot) => {
          setProfile(snapshot.val());
          setLoading(false);
        });

        const notifRef = ref(db, `notifications/${user.uid}`);
        onValue(notifRef, (snapshot) => {
          const data = snapshot.val();
          if (data) {
            const unread = Object.values(data).filter(n => !n.isRead).length;
            setUnreadCount(unread);
          } else {
            setUnreadCount(0);
          }
        });
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    const handleGlobalInput = (e) => {
      const target = e.target;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        // Skip chat inputs based on class name
        if (target.className && typeof target.className === 'string' && target.className.toLowerCase().includes('chat')) return;

        const emojiRegex = /[\u{1f300}-\u{1f5ff}\u{1f600}-\u{1f64f}\u{1f680}-\u{1f6ff}\u{1f1e6}-\u{1f1ff}\u{2700}-\u{27bf}\u{1f900}-\u{1f9ff}\u{1f3fb}-\u{1f3ff}\u{2600}-\u{26ff}\u{1f100}-\u{1f1ff}]/gu;
        if (emojiRegex.test(target.value)) {
          const newVal = target.value.replace(emojiRegex, '');
          if (target.value !== newVal) {
            target.value = newVal;
            const event = new Event('input', { bubbles: true });

            const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, "value")?.set;
            const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, "value")?.set;

            if (target.tagName === 'INPUT' && nativeInputValueSetter) {
              nativeInputValueSetter.call(target, newVal);
            } else if (target.tagName === 'TEXTAREA' && nativeTextAreaValueSetter) {
              nativeTextAreaValueSetter.call(target, newVal);
            }
            target.dispatchEvent(event);
          }
        }
      }
    };

    document.addEventListener('input', handleGlobalInput, true);
    return () => {
      unsubscribe();
      document.removeEventListener('input', handleGlobalInput, true);
    };
  }, []);

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to log out?')) {
      sessionStorage.removeItem('td_selectedPropertyId');
      sessionStorage.removeItem('td_bookingRoom');
      sessionStorage.removeItem('td_activeTab');
      signOut(auth);
      setView('dashboard');
      setAuthView('login');
    }
  };

  if (loading || verifyingLink) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--light-bg)', gap: '20px' }}>
        <div style={{ position: 'relative', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="loader" style={{ width: '60px', height: '60px' }}></div>
          <span style={{ position: 'absolute', fontSize: '20px' }}>🏖️</span>
        </div>
        <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: 'var(--text-muted)', letterSpacing: '0.5px' }}>
          Loading ResortsConnect...
        </p>
      </div>
    );
  }

  if (linkError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--light-bg)', padding: '20px', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--danger-color)', marginBottom: '15px', fontFamily: 'Outfit, sans-serif' }}>Link Expired</h2>
        <p style={{ color: '#666', marginBottom: '20px', maxWidth: '400px' }}>{linkError}</p>
        <button 
          onClick={() => {
            setLinkError(null);
            setAuthView('login');
            window.history.replaceState({}, document.title, window.location.pathname);
          }}
          style={{ padding: '12px 24px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
        >
          Return to Login
        </button>
      </div>
    );
  }

  if (requireEmailForLink) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', background: 'var(--light-bg)', padding: '20px', textAlign: 'center' }}>
        <h2 style={{ color: 'var(--primary)', marginBottom: '15px', fontFamily: 'Outfit, sans-serif' }}>Verify Your Email</h2>
        <p style={{ color: '#666', marginBottom: '20px', maxWidth: '400px' }}>
          For security purposes, please confirm the email address you used to request this link.
        </p>
        <input 
          type="email" 
          id="link-email-input"
          placeholder="Enter your email" 
          style={{ padding: '12px', width: '100%', maxWidth: '300px', borderRadius: '8px', border: '1px solid #ddd', marginBottom: '15px', outline: 'none' }}
        />
        <button 
          onClick={() => {
            const email = document.getElementById('link-email-input').value;
            if (!email) return;
            setRequireEmailForLink(false);
            setVerifyingLink(true);
            signInWithEmailLink(auth, email, window.location.href)
              .then((result) => {
                window.localStorage.removeItem('emailForSignIn');
                setAuthView('resetPassword');
                window.history.replaceState({}, document.title, window.location.pathname);
                setVerifyingLink(false);
              })
              .catch((error) => {
                console.error('Error signing in with email link', error);
                if (error.code === 'auth/invalid-action-code') {
                  setLinkError('This password reset link has expired or has already been used. Please request a new link.');
                } else if (error.code === 'auth/invalid-email') {
                  setLinkError('The email you entered is incorrect. Please try again.');
                } else {
                  setLinkError(error.message);
                }
                setVerifyingLink(false);
              });
          }}
          style={{ padding: '12px 24px', background: 'var(--primary)', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', transition: 'background 0.3s' }}
        >
          Verify Link
        </button>
      </div>
    );
  }

  if (user && authView === 'resetPassword') {
    return (
      <div style={{ position: 'relative', width: '100%', minHeight: '100vh' }}>
        <ResetPassword onBackToLogin={() => { 
          setAuthView('login'); 
          signOut(auth);
        }} />
      </div>
    );
  }

  if (!user) {
    if (authView === 'home') {
      return (
        <Homepage
          onLogin={() => setAuthView('login')}
          onRegister={() => setAuthView('register')}
          isDarkMode={isDarkMode}
          onToggleDark={() => setIsDarkMode(!isDarkMode)}
          onViewPolicies={() => setAuthView('policies')}
        />
      );
    }

    if (authView === 'policies' || (typeof authView === 'object' && authView.name === 'property_policies')) {
      return (
        <PoliciesPropertyDetails
          property={typeof authView === 'object' ? authView.property : null}
          onBack={() => setAuthView('home')}
        />
      );
    }

    let authComponent;
    if (authView === 'register') {
      authComponent = <Register onBackToLogin={() => setAuthView('login')} onGoHome={() => setAuthView('home')} />;
    } else if (authView === 'forgotPassword') {
      authComponent = <ForgotPassword onBack={() => setAuthView('login')} onGoHome={() => setAuthView('home')} />;
    } else if (authView === 'resetPassword') {
      authComponent = <ResetPassword onBackToLogin={() => setAuthView('login')} />;
    } else {
      authComponent = <Login onShowRegister={() => setAuthView('register')} onShowForgotPassword={() => setAuthView('forgotPassword')} onGoHome={() => setAuthView('home')} />;
    }

    return (
      <div style={{ position: 'relative', width: '100%', minHeight: '100vh' }}>
        <button
          onClick={() => setIsDarkMode(!isDarkMode)}
          style={{
            position: 'fixed', top: '20px', right: '20px', width: '46px', height: '46px',
            borderRadius: '50%', border: '1.5px solid var(--border)', background: 'var(--surface)',
            color: 'var(--text-main)', cursor: 'pointer',
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            transition: 'var(--transition)', zIndex: 10000, boxShadow: '0 6px 18px rgba(0,0,0,0.25)'
          }}
          className="theme-toggle-btn"
          title="Toggle Light / Dark Mode"
        >
          {isDarkMode ? <Sun size={20} color="#F59E0B" /> : <Moon size={20} color="var(--text-main)" />}
        </button>
        <button
          onClick={() => setAuthView('home')}
          style={{
            position: 'fixed', top: '20px', left: '20px', padding: '10px 20px',
            borderRadius: '50px', border: '1.5px solid var(--border)', background: 'var(--surface)',
            color: 'var(--text-main)', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: '8px',
            fontWeight: 800, fontSize: '14px', transition: 'var(--transition)', zIndex: 10000,
            boxShadow: '0 6px 18px rgba(0,0,0,0.25)'
          }}
          className="theme-toggle-btn"
          title="Return to Home"
        >
          ← Home
        </button>
        {authComponent}
        <style>{`
          .theme-toggle-btn:hover { transform: scale(1.06); filter: brightness(1.1); }
        `}</style>
      </div>
    );
  }

  const isSocialAuth = user.providerData && user.providerData.some(
    p => p.providerId === 'google.com' || p.providerId === 'facebook.com'
  );

  if (!user.emailVerified && !isSocialAuth) {
    return <VerifyEmail />;
  }

  if (profile?.isBanned) {
    return (
      <div className="app-container" style={{ textAlign: 'center', marginTop: '100px' }}>
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <h1 style={{ color: 'var(--primary)', marginBottom: '16px' }}>Account Suspended</h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>Please contact admin at resortconnect2026@gmail.com</p>
          <button
            className="btn btn-primary"
            onClick={handleLogout}
            style={{ marginTop: '20px' }}
          >
            Log out & Go Back
          </button>
        </div>
      </div>
    );
  }

  const isMissingDocs = profile && !profile.idImageUrl && profile.role !== 'Admin' && profile.identityStatus !== 'rejected';

  if (user && (!profile || isMissingDocs)) {
    if (!profile && !isSocialAuth) {
      // Don't show confirm prompt when no profile exists
      const forceLogout = () => {
        signOut(auth);
        setAuthView('login');
      };

      // Auto-logout if loading has already completed to prevent stuck state
      if (!loading) {
        setTimeout(() => signOut(auth), 0);
      }

      return (
        <div className="app-container" style={{ textAlign: 'center', marginTop: '100px' }}>
          <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
            <h1 style={{ color: '#ef4444', marginBottom: '16px' }}>Account Not Found</h1>
            <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>There is no profile associated with this account. The account may have been removed.</p>
            <button className="btn btn-primary" onClick={forceLogout} style={{ marginTop: '20px' }}>Back to Login</button>
          </div>
        </div>
      );
    }

    const socialSource = sessionStorage.getItem('socialSource');
    if (socialSource === 'login' && !proceedSocial) {
      return (
        <div className="app-container" style={{ textAlign: 'center', marginTop: '100px' }}>
          <div className="card" style={{ maxWidth: '500px', margin: '0 auto', padding: '32px' }}>
            <h1 style={{ color: '#ef4444', marginBottom: '16px' }}>Account Not Found</h1>
            <p style={{ color: 'var(--text-muted)', marginBottom: '32px', fontSize: '15px' }}>This email is not registered yet. Would you like to create an account?</p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <button className="btn" style={{ background: 'var(--light-bg)', color: 'var(--text-main)', border: '1px solid var(--border)', flex: 1 }} onClick={handleLogout}>No, cancel</button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={() => setProceedSocial(true)}>Yes, create account</button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div style={{ position: 'relative', width: '100%', minHeight: '100vh' }}>
        <Register onBackToLogin={handleLogout} onGoHome={() => setAuthView('home')} isCompletingSocial={true} socialUser={user} />
      </div>
    );
  }

  if (profile && profile.identityStatus === 'rejected' && profile.role !== 'Admin') {
    return <ResubmitDocuments user={user} profile={profile} onLogout={handleLogout} />;
  }

  if (profile && profile.role !== 'Admin' && profile.idVerified === false) {
    return (
      <div className="app-container" style={{ textAlign: 'center', marginTop: '100px' }}>
        <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
          <h1 style={{ color: 'var(--primary)', marginBottom: '16px' }}>Pending Verification</h1>
          <p style={{ color: 'var(--text-muted)', marginBottom: '24px' }}>Your account is currently pending Admin verification. Please wait until your Valid ID is approved.</p>
          <button
            className="btn btn-primary"
            onClick={handleLogout}
            style={{ marginTop: '20px' }}
          >
            Log out & Go Back
          </button>
        </div>
      </div>
    );
  }

  const role = (profile?.role || 'Tourist').toUpperCase();

  const renderContent = () => {
    if (view === 'profile') return <Profile onBack={() => setView('dashboard')} />;
    if (view === 'notifications') return <Notifications uid={user.uid} onBack={() => setView('dashboard')} />;
    if (view === 'policies') return <PoliciesPropertyDetails onBack={() => setView('dashboard')} ownerUid={role === 'OWNER' ? user.uid : null} />;
    if (view === 'edit_property') return (
      <>
        <OwnerDashboard profile={profile} uid={user.uid} />
        <EditPropertyModal uid={user.uid} onClose={() => setView('dashboard')} />
      </>
    );

    if (role === 'OWNER') return <OwnerDashboard key={dashboardKey} profile={profile} uid={user.uid} />;
    if (role === 'ADMIN') return <AdminDashboard key={dashboardKey} profile={profile} uid={user.uid} />;
    return <TouristDashboard key={dashboardKey} profile={profile} uid={user.uid} onViewPolicies={(prop) => setView({ name: 'property_policies', property: prop })} onEditProfile={() => setView('profile')} />;
  };

  if (view && view.name === 'property_policies') {
    return (
      <div className="app-container">
        <PoliciesPropertyDetails property={view.property} onBack={() => setView('dashboard')} />
      </div>
    );
  }

  return (
    <div className="app">
      {/* Global Premium Background */}
      <div className="aurora-wrapper">
        <div className="aurora-1"></div>
        <div className="aurora-2"></div>
        <div className="aurora-3"></div>
      </div>
      <header style={{
        backgroundColor: 'var(--nav-bg)',
        backdropFilter: 'blur(20px)',
        padding: '10px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        boxShadow: '0 4px 30px rgba(0,0,0,0.03)',
        position: 'sticky',
        top: 0,
        zIndex: 100,
        borderBottom: '1px solid var(--border)',
        boxSizing: 'border-box',
        width: '100%'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div className="logo-home-btn" style={{ cursor: 'pointer', position: 'relative', display: 'flex' }} onClick={() => { sessionStorage.removeItem('td_selectedPropertyId'); sessionStorage.removeItem('td_bookingRoom'); window.location.href = window.location.pathname; }}>
            <img src={logo} alt="Logo" className="nav-logo-img" style={{ height: '52px', width: 'auto' }} />
            <div className="logo-hover-overlay" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(255,255,255,0.1)', borderRadius: '12px', opacity: 0, transition: 'var(--transition)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
              <span style={{ background: 'var(--primary)', color: 'white', padding: '4px 8px', borderRadius: '8px', fontSize: '11px', fontWeight: 'bold', boxShadow: '0 2px 8px rgba(0,0,0,0.2)' }}>Dashboard</span>
            </div>
          </div>
          <div className="hide-mobile">
            <h2 style={{ margin: 0, fontSize: '17px', fontWeight: 800, color: 'var(--nav-title)', letterSpacing: '-0.5px' }}>Resort Connect</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--secondary)' }}></div>
              <p style={{ margin: 0, fontSize: '11px', fontWeight: 700, color: 'var(--secondary)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {profile?.firstName} • {role}
              </p>
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div className="nav-group" style={{
            background: 'var(--nav-group-bg)',
            padding: '4px',
            borderRadius: '30px',
            display: 'flex',
            gap: '3px'
          }}>
            <NavIcon icon={<LayoutDashboard size={18} />} active={view === 'dashboard'} onClick={() => setView('dashboard')} />

            {role !== 'ADMIN' && (
              <div style={{ position: 'relative' }}>
                <NavIcon icon={<Bell size={18} />} active={view === 'notifications'} onClick={() => setView('notifications')} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: '2px', right: '2px', background: 'var(--primary)',
                    color: 'white', fontSize: '9px', borderRadius: '50%',
                    minWidth: '15px', height: '15px', display: 'flex',
                    justifyContent: 'center', alignItems: 'center', fontWeight: 800,
                    border: '2px solid white'
                  }}>
                    {unreadCount}
                  </span>
                )}
              </div>
            )}
            <NavIcon icon={<ShieldAlert size={18} />} active={view === 'policies'} onClick={() => setView('policies')} />

            {role === 'OWNER' ? (
              <NavIcon icon={<Home size={18} />} active={view === 'edit_property'} onClick={() => setView('edit_property')} />
            ) : (
              <NavIcon icon={<User size={18} />} active={view === 'profile'} onClick={() => setView('profile')} />
            )}
          </div>

          <div style={{ width: '1px', height: '20px', background: 'var(--nav-divider)' }}></div>

          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            style={{
              width: '36px', height: '36px', borderRadius: '50%', border: 'none',
              background: 'var(--nav-group-bg)', color: 'var(--text-muted)', cursor: 'pointer',
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              transition: 'var(--transition)'
            }}
          >
            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          <button
            onClick={handleLogout}
            style={{
              width: '36px', height: '36px', borderRadius: '50%', border: 'none',
              background: 'var(--logout-bg)', color: 'var(--primary)', cursor: 'pointer',
              display: 'flex', justifyContent: 'center', alignItems: 'center',
              transition: 'var(--transition)'
            }}
            className="logout-btn"
          >
            <LogOut size={18} />
          </button>
        </div>
      </header>

      <main className={`app-container ${role === 'ADMIN' ? 'admin-container' : ''}`}>
        {renderContent()}
      </main>

      {user && role !== 'ADMIN' && (
        <ChatHeadBubble currentUid={user.uid} />
      )}

      <style>{`
        .logout-btn:hover { background: var(--primary) !important; color: white !important; transform: rotate(10deg); }
        .logo-home-btn { padding: 4px; border-radius: 12px; transition: var(--transition); }
        .logo-home-btn:hover { background: var(--nav-group-bg); transform: scale(1.02); }
        .logo-home-btn:hover .logo-hover-overlay { opacity: 1 !important; }
        @media (max-width: 600px) {
          .hide-mobile { display: none !important; }
          .nav-logo-img { height: 42px !important; }
          header { padding: 8px 12px !important; }
        }
      `}</style>
    </div>
  );
}

const NavIcon = ({ icon, onClick, active }) => (
  <button
    onClick={onClick}
    style={{
      background: active ? 'var(--nav-logo-bg)' : 'transparent',
      border: 'none',
      borderRadius: '50%',
      width: '38px',
      height: '38px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      cursor: 'pointer',
      color: active ? 'var(--primary)' : 'var(--text-muted)',
      transition: 'var(--transition)',
      boxShadow: active ? '0 4px 10px rgba(0,0,0,0.05)' : 'none'
    }}
  >
    {icon}
  </button>
);

export default App;
